import { Label } from "@radix-ui/react-label";
import { useId, useMemo } from "react";
import {
    useFormContext,
    useController,
    type FieldValues,
    type Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import ComboBox, {
    ComboboxItem,
    QueryFnParams,
    PaginationResponse,
} from "../combo-box/ComboBox";
import { useSchema } from "@/v2/hooks/SchemaContext";
import RequiredMark from "./RequiredMark";
import { ZodArray } from "zod";

interface ArFormComboboxProps<
    TFieldValues extends FieldValues,
    TItem extends ComboboxItem,
> {
    // Form field props
    name: Path<TFieldValues>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
    // Combobox query configuration
    queryKey: string | string[];
    queryFn: (params: QueryFnParams) => Promise<PaginationResponse<TItem>>;
    additionalFilters?: Record<string, any>;
    // Display configuration
    getDisplayValue: (item: TItem) => string;
    renderItem?: (item: TItem) => React.ReactNode;
    // UI customization
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    width?: string;
    disabled?: boolean;
    // Optional: Get the full object without storing it in form state
    onItemSelect?: (item: TItem | null) => void;
    // Query options
    pageSize?: number;
    debounceMs?: number;
    // Optional: If you need to work with the full item object in form
    // By default, only the ID is stored in the form
    storeFullObject?: boolean;
    // NEW: Allow parent to pass initial value directly
    defaultValueId?: string;
    defaultValue?: TItem | null;
}

export function ArFormCombobox<
    TFieldValues extends FieldValues,
    TItem extends ComboboxItem,
>({
    name,
    label,
    labelAlign,
    queryKey,
    queryFn,
    additionalFilters,
    getDisplayValue,
    renderItem,
    placeholder = "Select item...",
    searchPlaceholder = "Search...",
    className,
    width,
    disabled,
    pageSize,
    debounceMs,
    onItemSelect,
    storeFullObject = false,
    defaultValueId,
    defaultValue,
}: ArFormComboboxProps<TFieldValues, TItem>) {
    const {
        control,
        formState: { errors },
    } = useFormContext<TFieldValues>();

    const {
        field: { value, onChange },
    } = useController({
        name,
        control,
    });

    const error = errors[name as keyof typeof errors];
    const id = useId();
    const schema = useSchema();
    const isRequired = useMemo(() => {
        const parts = name.split(".");
        let currentSchema: any = schema;

        for (const part of parts) {
            if (!currentSchema?.shape) return false;

            // Handle array index (numeric parts)
            if (/^\d+$/.test(part)) {
                if (currentSchema instanceof ZodArray) {
                    currentSchema = currentSchema.element;
                }
                continue;
            }

            currentSchema = currentSchema.shape[part];

            if (currentSchema instanceof ZodArray) {
                currentSchema = currentSchema.element;
            }
        }

        const testResult = currentSchema?.safeParse("");
        return !testResult?.success;
    }, [schema, name]);

    // Determine initial value with priority: defaultValueId > form value
    const getInitialValue = () => {
        if (defaultValueId) {
            return defaultValueId;
        }

        if (storeFullObject) {
            return (value as TItem)?.id;
        }

        return value as string | undefined;
    };

    const initialItem = storeFullObject ? (value as TItem) : undefined;

    const handleSelect = (item: TItem | null) => {
        if (storeFullObject) {
            onChange(item);
        } else {
            onChange(item?.id || null);
        }
        if (onItemSelect) {
            onItemSelect(item);
        }
    };

    return (
        <div className={cn("flex flex-col gap-1", labelAlign && labelAlign)}>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <ComboBox<TItem>
                defaultValue={defaultValue}
                queryKey={queryKey}
                queryFn={queryFn}
                additionalFilters={additionalFilters}
                getDisplayValue={getDisplayValue}
                renderItem={renderItem}
                onSelect={handleSelect}
                initialValue={getInitialValue()}
                initialItem={initialItem}
                placeholder={placeholder}
                searchPlaceholder={searchPlaceholder}
                className={cn(
                    error &&
                        "border-destructive focus-visible:ring-destructive",
                    className,
                )}
                width={width}
                disabled={disabled}
                pageSize={pageSize}
                debounceMs={debounceMs}
            />
            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}

export default ArFormCombobox;
